import { getCsrfToken } from './csrf';
import { ApiError, networkError, toApiError } from './errors';

/**
 * The one way this app talks to social-admin.
 *
 * - Same origin (`/api`, proxied by Vite in development and nginx in production), so the
 *   session cookie travels without CORS.
 * - Every mutating request carries the CSRF header.
 * - Every failure becomes an {@link ApiError}. A 401 additionally notifies the registered
 *   handler, which ends the local session and sends the user to the sign-in page.
 */
export const API_BASE = '/api';

type Primitive = string | number | boolean;
export type QueryParams = Record<string, Primitive | null | undefined | readonly Primitive[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: QueryParams;
  /** Plain objects are sent as JSON; FormData and URLSearchParams are sent as they are. */
  body?: unknown;
  signal?: AbortSignal;
  /** Do not treat a 401 as an expired session (sign-in and the session probe). */
  skipUnauthorizedHandler?: boolean;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

let unauthorizedHandler: (() => void) | null = null;
let mustChangePasswordHandler: (() => void) | null = null;

/** Called on 403 must_change_password: the password was reset under an open session. */
export function setMustChangePasswordHandler(handler: (() => void) | null): void {
  mustChangePasswordHandler = handler;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204 || response.status === 205) return undefined as T;
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('json')) return (await response.json()) as T;
  const text = await response.text();
  return (text === '' ? undefined : text) as T;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

/** For file responses (CSV exports): returns the body as a Blob plus the server's file name. */
export async function download(
  path: string,
  options: RequestOptions = {},
): Promise<DownloadedFile> {
  const response = await send(path, options);
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition')),
  };
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers = new Headers({
    Accept: 'application/json',
    // Tells Spring Security this is an API call: answer 401, never redirect to a login page.
    'X-Requested-With': 'XMLHttpRequest',
  });

  let body: BodyInit | undefined;
  if (options.body instanceof FormData || options.body instanceof URLSearchParams) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  if (!SAFE_METHODS.has(method)) {
    let csrf;
    try {
      csrf = await getCsrfToken();
    } catch (cause) {
      throw networkError(cause);
    }
    headers.set(csrf.headerName, csrf.token);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: body ?? null,
      credentials: 'same-origin',
      signal: options.signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw networkError(cause);
  }

  if (response.ok) return response;

  const error = toApiError(
    response.status,
    await readBody(response),
    response.headers.get('X-Request-Id'),
  );
  if (error.isUnauthorized && !options.skipUnauthorizedHandler) unauthorizedHandler?.();
  if (error.status === 403 && error.code === 'must_change_password') mustChangePasswordHandler?.();
  throw error;
}

export function buildUrl(path: string, query?: QueryParams): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    const values: readonly Primitive[] = Array.isArray(value) ? value : [value as Primitive];
    for (const item of values) params.append(key, String(item));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '');
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) return decodeURIComponent(encoded[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

export { ApiError };
